// }
import {
  ComplexMeasurementUnits,
  FindingSource,
  QualifiedFeature,
  QualifiedFeatureFeature,
  QualifiedFeatureFragment,
  QualifiedFeatureQualifiers,
  QualifiedFeatureRelation,
  QualifierValue,
} from "../../graphql";
import {
  FREE_TEXT_FEATURE_ID,
  GenderValue,
  isPopulationFeature,
  Relations,
} from "shared";
import { DDClinicalReasoning } from "../../graphql/dao";

export interface FeatureTerm {
  feature: QualifiedFeatureFeature | QualifiedFeatureRelation;
  negate: boolean;
  value?: QualifiedFeatureQualifiers;
}

type crToQualifiedFeatureFunc = (
  cr: DDClinicalReasoning,
) => QualifiedFeatureFragment;

export const crToQualifiedFeature = (cr) => ({
  qf: cr.disease[0],
  crs: [cr],
  flags: cr.flags.map((f) => f.type),
  citations: cr.citations,
});

export function featureToTerms(
  qf: QualifiedFeatureFragment,
  relationsToIgnore: string[] = [],
): Array<FeatureTerm> {
  let result = [];
  let { feature, qualifiers, mandatory, negate } = qf;

  // if we have a procedure, use that in place of mandatory qualifier
  if (!mandatory) {
    mandatory = getQualifierByRelation(qf, Relations.PROCEDURE_FINDING);
  }

  // push feature, possibly with a  mandatory relation
  if (feature.id !== FREE_TEXT_FEATURE_ID) {
    result.push({
      feature: feature,
      negate: negate,
      value: mandatory,
    });
  }

  // now push each qualifier
  if (qualifiers) {
    qualifiers
      .filter((q) => !relationsToIgnore.includes(q.relation?.id) && q.value)
      .forEach((q) => {
        // skip mandatory
        if (
          mandatory &&
          mandatory.relation &&
          mandatory.relation?.id == q.relation?.id
        ) {
          return;
        }
        result.push({
          negate: q.negate,
          feature: q.relation,
          value: q,
        });
      });
  }

  return result;
}

function mainName(e: { name: string; alias?: string; shortname?: string }) {
  let name = e ? e.alias || e.name : null;

  if (e?.name === "Drug") {
    return "Medications";
  }

  return name;
}

function patientFacingName(e: {
  name: string;
  alias?: string;
  shortname?: string;
}) {
  return e?.alias || e?.shortname;
}

export const ComplexUnitsToString = (complexUnits: ComplexMeasurementUnits) => {
  return (
    complexUnits?.numerator?.join("*") +
    (!complexUnits?.denominator?.length
      ? ""
      : "/" + complexUnits?.denominator?.join("*"))
  );
};

export function valueName(q: QualifiedFeatureQualifiers) {
  if (q?.freeText) return `*${q.freeText}*`;
  if (q?.range) {
    if (q.range.low === q.range.high) {
      return `${q.range.low} ${q.range.units ||
        ComplexUnitsToString(q.range.complexUnits)}`;
    } else {
      return `${q.range.low}-${q.range.high} ${q.range.units ||
        ComplexUnitsToString(q.range.complexUnits)}`;
    }
  }
  return q?.value && capitalize(mainName(q.value));
}
export function valueRelation(q: QualifiedFeatureQualifiers) {
  return capitalize(q?.relation?.name);
}

export function valueNameForSummary(q: QualifiedFeatureQualifiers) {
  return valueName(q)?.toLowerCase() || "";
}

function capitalize(s: string) {
  if (!s) return null;
  const lowerCaseName = s.toLocaleLowerCase();
  return `${lowerCaseName.charAt(0).toUpperCase() + lowerCaseName.slice(1)} `;
}

function termToText(
  t: FeatureTerm,
  relationsToIgnore: string[] = [],
  featuresToOmit: string[] = [],
  patientFacing: boolean = false,
  isForm = false,
) {
  const { feature, negate, value } = t;

  let featureStr = patientFacing
    ? patientFacingName(feature) || ""
    : mainName(feature) || "";

  if (isForm && featureStr !== "") {
    featureStr = featureStr + ": ";
  }

  const sourceMark =
    value?.source == FindingSource.Implied
      ? " (i)"
      : value?.source == FindingSource.Possible
      ? " (p)"
      : "";
  const vn = valueName(value);
  const valueStr = vn ? vn + sourceMark : "";

  const negation = negate ? "No " : "";

  if (
    value?.relation?.id === Relations.LEVEL &&
    !relationsToIgnore.includes(Relations.LEVEL)
  ) {
    return `${negation}${valueStr} ${featureStr}`;
  } else if (
    value?.relation?.id === Relations.ALLERGY_REACTIONS ||
    value?.relation?.id === Relations.DOSAGE
  ) {
    return valueStr === "" ? valueStr : ` (${valueStr.trim()})`;
  } else if (
    value?.relation?.id === Relations.EXPOSURE_TO &&
    !relationsToIgnore.includes(Relations.EXPOSURE_TO)
  ) {
    return `${valueStr} | ${negation}Exposure`;
  } else if (
    value?.relation?.id === Relations.FAMILY_HISTORY &&
    !relationsToIgnore.includes(Relations.FAMILY_HISTORY)
  ) {
    return `${valueStr} | ${negation}Family History`;
  } else if (
    value?.relation?.id === Relations.PROCEDURE &&
    !relationsToIgnore.includes(Relations.PROCEDURE)
  ) {
    return `${valueStr} | ${negation}${featureStr}`;
  } else if (
    value?.relation?.id === Relations.PROCEDURE_FINDING &&
    !relationsToIgnore.includes(Relations.PROCEDURE_FINDING)
  ) {
    return `${negation}finding of ${valueStr}`;
  } else if (
    value?.relation?.id === Relations.GENDER &&
    !relationsToIgnore.includes(Relations.GENDER)
  ) {
    return value.value?.id === GenderValue.MALE ? "Male" : "Female";
  } else if (feature && valueStr && valueRelation(value)?.includes("istory")) {
    return `${negate ? "Not " : ""}${valueStr} | ${valueRelation(value)}`;
  } else if (feature && isPopulationFeature(feature) && valueStr) {
    return `${negate ? "Not " : ""}${valueStr}`;
  } else {
    if (feature) {
      if (featuresToOmit.includes(feature.id)) {
        return `${negation}${value ? " " : ""}${valueStr}`;
      } else {
        return `${negation}${featureStr}${value ? " " : ""}${valueStr}`;
      }
    } else {
      return "";
    }
  }
}

/** this should really be called, feature to text parts */
export function featureToText(qf: QualifiedFeatureFragment) {
  return featureToTerms(qf).map((p) => ({
    name: `${p.negate ? "No " : ""}${mainName(p.feature)}`,
    value: p.value && valueName(p.value) && `${valueName(p.value)}`,
  }));
}

export function featureToLines(
  qf: QualifiedFeatureFragment,
  skipFeature = false,
  relationsToIgnore?: string[],
  featuresToOmitName?: string[],
  patientFacing?: boolean, // Whether or not translate the name for patient facing or keep the app name for doctors
  isForm = false,
) {
  const text = featureToTerms(qf, relationsToIgnore).map(
    (term, i) =>
      termToText(
        i == 0 && skipFeature ? { ...term, feature: null } : term,
        relationsToIgnore,
        featuresToOmitName,
        patientFacing,
        isForm,
      ).replace("(finding)", ""), //Not sure why some features have a (finding) appended
  );

  return text;
}

// TBD redefine using featureToLines
export function featureTo2Lines(
  qf: QualifiedFeatureFragment,
  skipFeature = false,
  withValue = true,
  skipRelations = false,
) {
  let text = featureToText(qf);
  let primary =
    text.length > 0
      ? skipFeature
        ? `${text[0].value || ""}`
        : `${text[0].name}${
            withValue && text[0].value ? " " + text[0].value : ""
          }`
      : null;
  let secondary =
    text.length > 1 &&
    text
      .slice(1, text.length)
      .map(
        (q) => `${skipRelations ? "" : q.name}${q.value ? " " + q.value : ""}`,
      )
      .join("|");
  return [primary || null, secondary || null];
}

export function featuresToMultiLine(
  qfs: QualifiedFeatureFragment[],
  seperator = "\n",
  skipRelations = false,
) {
  return qfs
    .map((f) => featureTo2Lines(f, false, true, skipRelations))
    .filter((l) => l && l[0])
    .map(([p, s]) => `${p} ${s || ""}`)
    .join(seperator);
}

// persistent hash function for comparing qualified features
export function hashQF(qf: QualifiedFeatureFragment) {
  if (!qf) return null;
  return (qf as any)._hash || ((qf as any)._hash = qfID(qf));
}

function qfID(qf: QualifiedFeatureFragment) {
  let qualifiers = qf.qualifiers
    ? qf.qualifiers.map(
        (q) => `${q.relation && q.relation.id}${q.value && q.value.id}`,
      )
    : [];
  return [qf.feature ? qf.feature.id : "", ...qualifiers].join("|");
}

export function getQualifierByRelation(
  qf: QualifiedFeatureFragment,
  rel: Relations,
) {
  // TBD improve by also looking in stated qualifiers
  return qf?.qualifiers?.find((q) => q.relation?.id === rel);
}

export function getQualifiersValuesByRelation(
  qf: QualifiedFeatureFragment,
  rel: Relations,
) {
  return qf?.qualifiers
    ?.filter((q) => q.relation?.id === rel)
    .map((q) => valueNameForSummary(q).trim())
    .join(", ");
}

export const getFindingLevel = (finding: QualifiedFeature) =>
  finding.qualifiers.find(
    (qualifier) => qualifier.relation.id === Relations.LEVEL,
  )?.value?.id;

//For those relations we omit the relation name from the text. Example: hip and not Location hip
const relationOmitList = [
  Relations.LOCATION,
  Relations.APPEARANCE,
  Relations.ASSOCIATED_MORPHOLOGY,
  Relations.CHARACTER,
  Relations.CHARACTER_PITCH,
  Relations.INTENSITY,
  Relations.LATERALITY,
  Relations.LEVEL,
  Relations.OCCURANCE,
  Relations.PITCH,
  Relations.QUALITY,
  Relations.SEVERITY,
  Relations.SHAPE,
  Relations.SIDENESS,
  Relations.TIMING,
  Relations.PULSE_FINDING,
];

export const getQFAndRefinementsLines = (
  qf: QualifiedFeature,
  relationToIgnore = [],
) => {
  if (!qf) return ["", ""];

  const findingTitle = featureToLines(qf)[0];
  const qualifiersLinesText = featureToLines(
    qf,
    true,
    [Relations.PANEL_ID, Relations.LEVEL].concat(relationToIgnore),
    relationOmitList,
  )
    .filter((q) => q)
    .filter((q) => q.trim() !== findingTitle.trim());

  const findingQualifiersText = `${qualifiersLinesText.join(", ")}`;

  return [findingTitle, findingQualifiersText];
};

//retrieve a relation for which there is no value
export const getGenericRelationText = (qf: QualifiedFeature): string[] => {
  if (!qf) return [""];
  return qf.qualifiers.filter((q) => !q.value).map((q) => q.relation.name);
};

export const isOfCategory = (categoryId) => (qf: QualifiedFeature): boolean =>
  qf?.feature?.category?.id === categoryId;

export const isOfRelation = (relationId) => (qf: QualifiedFeature): boolean =>
  qf.mandatory?.relation?.id === relationId;

export const isNegate = (qf: QualifiedFeature) => qf.negate;

export const shortenFindingName = (
  f: string,
  totalClear: boolean = false,
): string => {
  return f
    ?.replace(" (disorder)", totalClear ? "" : " (d)")
    .replace(" (finding)", totalClear ? "" : " (f)")
    .replace(" (substance)", totalClear ? "" : " (s)")
    .replace(" (body structure)", totalClear ? "" : " (bs)")
    .replace(" (qualifier value)", totalClear ? "" : " (qf)")
    .replace(" (severity modifier)", totalClear ? "" : " (sm)")
    .replace(" (observable entity)", totalClear ? "" : " (obe)")
    .replace(" (morphologic abnormality)", totalClear ? "" : " (moab)")
    .replace("|Worsened by=", " Worsened by ")
    .replace("|Worsened by ", " Worsened by ")
    .replace("|Radiating to=", " Radiating ")
    .replace("|Radiating to ", " Radiating ")
    .replace("Population|Periods of life =:", "AGE ")
    .replace("Periods of life |Periods of life ", "AGE ")
    .replace("Population|History of=", "History of ")
    .replace("History of|History of ", "History of ")
    .replace("Population|Exposure to=", "Exposure to ")
    .replace("Exposure to|Exposure to ", "Exposure to ")
    .replace("|Location=", " in ")
    .replace("|Location ", " in ")
    .replace("|Triggered by= ", " Triggered by ");
};

export function buildQualifierValue(id: string): QualifierValue {
  return {
    id,
  } as any;
}
